import React from 'react';

/**
 * Shared primitives for the catalogue surface.
 *
 * Design register (see report): turbopuffer-style density rendered in the
 * site's existing DARK palette rather than turbopuffer's light ground. The
 * site's AppBar, Footer and fixed body gradient are all dark and are owned by
 * other agents, so a light catalogue surface would have collided with chrome
 * this page cannot change.
 *
 * Rules encoded here:
 *  - Monospace is the primary voice: labels, keys, every numeric, chips, code.
 *    Sans is reserved for prose paragraphs only.
 *  - Hairline borders, no fills, no decorative imagery.
 *  - Section labels notch into the top border, fieldset-legend style. This is
 *    why GROUND is a solid colour and not a gradient — the notch has to paint
 *    over the border with the exact page ground.
 *  - No entrance animation anywhere. Hover and focus feedback is instant
 *    (colour swaps only, no transition), which is what "snappy" means here.
 */

/** The catalogue's solid ground. Matches the tail stop of the body gradient. */
export const GROUND = '#0d1117';

/** Visible keyboard focus, quiet under a mouse. */
export const FOCUS =
  'focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400';

/** Hairline panel. No rounding beyond a hint; no fill. */
export const PANEL = 'relative border border-gray-800 bg-transparent';

/**
 * A panel whose heading is notched into its top border.
 * `level` picks the heading tag so the document outline stays correct.
 */
export const Panel: React.FC<{
  label: string;
  id: string;
  children: React.ReactNode;
  level?: 2 | 3;
  /** Optional right-aligned content in the notch line, e.g. a count. */
  aside?: React.ReactNode;
}> = ({ label, id, children, level = 2, aside }) => {
  const Heading = (level === 2 ? 'h2' : 'h3') as 'h2' | 'h3';
  return (
    <section
      aria-labelledby={id}
      className={`${PANEL} px-4 pt-7 pb-5 sm:px-6 sm:pb-6`}
    >
      <Heading
        id={id}
        className="absolute -top-[9px] left-4 sm:left-6 px-2 font-mono text-[11px] uppercase tracking-[0.16em] text-blue-400"
        style={{ backgroundColor: GROUND }}
      >
        {label}
      </Heading>
      {aside ? (
        <div
          className="absolute -top-[9px] right-4 sm:right-6 px-2 font-mono text-[11px] uppercase tracking-[0.16em] text-gray-400"
          style={{ backgroundColor: GROUND }}
        >
          {aside}
        </div>
      ) : null}
      {children}
    </section>
  );
};

/** Monospace chip. `tone` distinguishes a fact from a published gap. */
export const Chip: React.FC<{
  children: React.ReactNode;
  tone?: 'default' | 'accent' | 'beta' | 'gap';
}> = ({ children, tone = 'default' }) => {
  const tones: Record<string, string> = {
    default: 'border-gray-700 text-gray-300',
    accent: 'border-blue-400/50 text-blue-300',
    beta: 'border-yellow-500/50 text-yellow-300',
    // Gaps are dashed and muted — present, legible, plainly incomplete.
    gap: 'border-gray-700 border-dashed text-gray-400',
  };
  return (
    <span
      className={`inline-flex items-center border px-2 py-[3px] font-mono text-[11px] tracking-tight ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

/** Inline link inside data. Underlined, per the reference. */
export const DataLink: React.FC<{
  href: string;
  children: React.ReactNode;
  external?: boolean;
}> = ({ href, children, external = true }) => (
  <a
    href={href}
    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    className={`text-green-400 underline underline-offset-2 hover:text-green-300 ${FOCUS}`}
  >
    {children}
    {external ? <span className="sr-only"> (opens in a new tab)</span> : null}
  </a>
);
